import { useEffect, useMemo, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useI18n } from '../../../provider/i18n'
import Input from '../../../components/Input'
import useBasketData from '../../../hooks/useBasketData'
import { MdDelete } from 'react-icons/md'
import { Red800 } from '../../../lib/colors'

type DiscountInputProps = {
  value?: string
  label: string
  placeholder?: string
  error?: string
  confirmText: string
  onSubmit: (value?: string) => void
  onFocus: () => void
}

type DiscountsErrors = {
  discount: boolean
  kdr: boolean
}

const DiscountInput = ({
  value,
  label,
  placeholder,
  confirmText,
  error,
  onSubmit,
  onFocus,
}: DiscountInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const onConfirm = () => {
    if (inputRef.current) {
      const newValue = inputRef.current.value
      newValue && onSubmit(newValue)
      inputRef.current.value = ''
    }
  }

  const onDelete = () => {
    onSubmit()
  }

  return (
    <div
      className={
        'flex justify-between items-center bg-white border-b border-b-border border-opacity-20 p-2 md:px-4 rounded-lg min-h-16'
      }
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="text-base text-blue-700">{label}</span>
        <Input
          ref={inputRef}
          placeholder={placeholder}
          onFocus={onFocus}
          className={twMerge(
            'p-2 bg-blue-100 rounded-lg border-b-border border-opacity-20 w-[200px] text-base',
            !!value && 'hidden'
          )}
        />
      </div>
      {value ? (
        <div className="flex gap-4">
          <div className="flex flex-col al">
            <span className="text-blue-700 font-bold text-end">
              {value.startsWith('KDR:')
                ? value.split(':')[1].toUpperCase()
                : value.toUpperCase()}
            </span>
            {!!error && <span className="text-red-700 text">{error}</span>}
          </div>
          <button onClick={onDelete}>
            <MdDelete size={24} color={Red800} />
          </button>
        </div>
      ) : (
        <button onClick={onConfirm} className="hl-button">
          {confirmText}
        </button>
      )}
    </div>
  )
}

const Discounts = () => {
  const i18n = useI18n()
  const { orderCost, discountCodes, setDiscountCodes, verifyKDR } =
    useBasketData()
  const [errors, setErrors] = useState<DiscountsErrors>({
    discount: false,
    kdr: false,
  })

  const currentDiscountCode = useMemo(
    () => (discountCodes.length ? discountCodes[0] : undefined),
    [discountCodes]
  )

  useEffect(() => {
    const isValid =
      !!discountCodes.length &&
      !!discountCodes.find(
        (dCode) =>
          !orderCost ||
          orderCost.discounts.find(
            ({ code }) => dCode.toUpperCase() === code.toUpperCase()
          )
      )
    setErrors((errors) => ({ ...errors, discount: !isValid }))
  }, [orderCost, discountCodes])

  const onDiscountCodeSubmit = (code?: string) => {
    setDiscountCodes(code ? [code] : [])
  }

  const onDiscountInputFocus = () =>
    errors.discount && setErrors((errors) => ({ ...errors, discount: false }))

  const onKDRCodeSubmit = async (code?: string) => {
    if (code) {
      setDiscountCodes([`KDR:${code}`])
      const { data } = await verifyKDR({ cardNumber: code })
      const isValid = !!data?.client_verify_kdr?.valid
      if (!isValid) {
        !errors.kdr && setErrors((errors) => ({ ...errors, kdr: true }))
      }
    } else {
      setDiscountCodes([])
    }
  }

  const onKDRInputFocus = () =>
    errors.kdr && setErrors((errors) => ({ ...errors, kdr: false }))

  const CodeInput = (
    <DiscountInput
      value={currentDiscountCode}
      label={i18n.t('screens.basket.sections.discounts.discountInput.label')}
      placeholder={i18n.t(
        'screens.basket.sections.discounts.discountInput.placeholder'
      )}
      confirmText={i18n.t('common.confirm')}
      error={
        errors.discount
          ? i18n.t('screens.basket.form.discountCode.invalid')
          : undefined
      }
      onSubmit={onDiscountCodeSubmit}
      onFocus={onDiscountInputFocus}
    />
  )

  const KDRInput = (
    <DiscountInput
      value={currentDiscountCode}
      label={i18n.t('screens.basket.sections.discounts.kdrInput.label')}
      placeholder={i18n.t(
        'screens.basket.sections.discounts.kdrInput.placeholder'
      )}
      confirmText={i18n.t('common.confirm')}
      error={
        errors.kdr
          ? i18n.t('screens.basket.kdrPrompt.validator.invalidCode')
          : undefined
      }
      onSubmit={onKDRCodeSubmit}
      onFocus={onKDRInputFocus}
    />
  )

  return (
    <div className="flex flex-col gap-2">
      <span className="text-lg md:text-xl font-bold">
        {i18n.t('screens.basket.sections.discounts.title')}
      </span>
      {!currentDiscountCode ? (
        <>
          {CodeInput}
          {KDRInput}
        </>
      ) : currentDiscountCode.startsWith('KDR:') ? (
        KDRInput
      ) : (
        CodeInput
      )}
    </div>
  )
}

export default Discounts
